import { useCallback, useMemo, useRef } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import { getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { Add01Icon, Delete01Icon } from '@hugeicons/core-solid-rounded'

import * as Form from '@/features/form'
import { graphqlToForm } from '@/features/translations'
import { useBranding } from '@/features/branding'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { Suspense } from '@/components/ui/Suspense'
import { FormData, useBanner } from '../hooks/useBanner'
import { useBanners } from '../hooks/useBanners'

function BannerForm({
  banner,
  bannerNameSet
}: {
  banner?: GraphQL.BannerFragmentFragment
  bannerNameSet: Set<string>
}) {
  const { t } = useLingui()
  const ref = useRef<Form.RefType<FormData>>(null)

  const {
    branding: { supportedLocales }
  } = useBranding()

  const name = banner?.name

  const {
    create,
    update,
    remove,
    createLoading,
    updateLoading,
    deleteLoading
  } = useBanner(banner?.id)

  const reset = useCallback(() => {
    ref.current?.reset({ name: '', translations: {} })
  }, [])

  return (
    <Form.Root<FormData>
      ref={ref}
      overrideClassName="flex flex-row gap-1"
      onSubmit={banner ? update : create}
      onSubmittedSuccessfully={banner ? () => null : reset}
      translatableFields={['name']}
      defaultValues={{
        name: banner?.name ?? '',
        translations: graphqlToForm(
          (banner?.translations ?? []).flatMap(trans =>
            getFragment(SharedAPI.translation.TRANSLATION_FRAGMENT, trans)
          ),
          ['name'],
          supportedLocales ?? []
        )
      }}
    >
      <div className="flex flex-row gap-2 items-end flex-1 bg-primary-bright rounded-small p-2">
        <Form.Field
          name="name"
          rules={{
            maxLength: {
              value: 255,
              message: t`Can't be longer than 255 characters.`
            },
            validate: {
              required: Form.Validations.requiredWithTrimValidation(
                t`This field is required`
              ),
              alreadyExists(value: string) {
                if (bannerNameSet.has(value)) return t`Banner already exists`
              }
            }
          }}
        >
          {({ renderProps: { onChange, ...props } }) => (
            <Input
              autoComplete="off"
              onChange={event => onChange(event.target.value)}
              type="text"
              {...props}
            />
          )}
        </Form.Field>
        {!banner && (
          <Form.Submit
            loading={createLoading}
            onlyValidWhenDirty
            Icon={Add01Icon}
          >
            <Trans>Add</Trans>
          </Form.Submit>
        )}
        {banner && (
          <>
            <Form.Submit loading={updateLoading} onlyValidWhenDirty>
              <Trans>Save</Trans>
            </Form.Submit>
            <ConfirmDialog
              title={t`Delete Banner`}
              message={t`Are you sure you want to delete the banner with the name "${name}", this cannot be reversed?`}
              onConfirm={remove}
              asChild
            >
              <Button
                variant="outline"
                type="button"
                aria-label={t`Delete`}
                Icon={Delete01Icon}
                loading={deleteLoading}
              />
            </ConfirmDialog>
          </>
        )}
      </div>
    </Form.Root>
  )
}

function BannersForm() {
  const banners = useBanners()

  const bannerNameSet = useMemo(() => {
    if (!banners) return new Set<string>()

    return new Set(banners.map(b => b.name))
  }, [banners])

  return (
    <div className="flex flex-col gap-2.5">
      <BannerForm bannerNameSet={bannerNameSet} />
      <div className="flex flex-col gap-2.5 overflow-y-auto">
        {banners?.map(function (banner) {
          return (
            <BannerForm
              bannerNameSet={bannerNameSet}
              banner={banner}
              key={banner.id}
            />
          )
        })}
      </div>
    </div>
  )
}

export function BannersFormSuspended() {
  return (
    <Suspense>
      <BannersForm />
    </Suspense>
  )
}
